import React, { useState, useRef } from 'react';
import Button from '@strava/ui/Button';
import * as EmailValidator from 'email-validator';
import ReCAPTCHA from 'react-google-recaptcha';
import { isNil } from 'lodash-es';
import Spinner from '@strava/ui/Spinner';
import {
  elemAttrForAnalytics,
  EMAIL,
  ThemeTypes
} from '@strava/authentication-ui';
import RegistrationInput from '../RegistrationInput';
import {
  submitFormData,
  isAlreadyRegisteredEmail
} from './email-registration-api';

import {
  useStravaErrorLogging,
  useStravaTranslations
} from '@strava/container-context';

import styles from './EmailRegistrationForm.module.scss';

const i18nPrefix = 'account_registration_page_ui';

export interface EmailRegistrationFormProps {
  reCaptchaSiteKey: string | null;
  clubId?: number;
  isPremium: boolean;
  challengeId?: number;
  autoJoinChallenge?: boolean;
  honeyPot: string;
  plan?: string;
  theme?: ThemeTypes;
  formAuthToken?: string;
}

export type AlertTypes = {
  severity: string;
  message: string;
} | null;

type FormInputTypes = {
  email: string;
  password: string;
  honeyPot: string;
};

type EmailValidationStateTypes = {
  message: string;
  isExistingEmail: boolean;
  isValidEmail: boolean;
};

export type AnalyticsFieldsTypes = {
  action: string;
  element?: string;
  properties?: Record<string, string | boolean | null>;
};

const EmailRegistrationForm = ({
  authorizationUrl,
  reCaptchaSiteKey,
  honeyPot,
  isPremium,
  clubId,
  plan,
  challengeId,
  autoJoinChallenge,
  formAuthToken,
  setAlert,
  track,
  className,
  theme = 'dark',
  idPrefix
}: EmailRegistrationFormProps & {
  authorizationUrl: string;
  setAlert: (obj: AlertTypes) => void;
  track: (fields: AnalyticsFieldsTypes) => void;
  className?: string;
  idPrefix: string;
}) => {
  const [formInput, setFormInput] = useState<FormInputTypes>({
    email: '',
    password: '',
    honeyPot: '' // if this field gets filled out, user is probably a bot
  });
  const [passwordErrorMsg, setPasswordErrMsg] = useState<string | null>(null);
  const [emailValidationState, setEmailValidationState] =
    useState<EmailValidationStateTypes | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const emailInput = useRef<HTMLInputElement>(null);
  const passwordInput = useRef<HTMLInputElement>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const useRecaptcha = !!reCaptchaSiteKey;

  const { logError } = useStravaErrorLogging();
  const { t } = useStravaTranslations();

  const getRecaptchaToken = async () => {
    if (useRecaptcha) {
      return (await recaptchaRef?.current?.executeAsync()) ?? '';
    }
    return '';
  };

  const handleEmailFormSubmit = async () => {
    const recaptchaToken = await getRecaptchaToken();

    const element = elemAttrForAnalytics(EMAIL);
    const { email, password, honeyPot } = formInput;

    const formData = new FormData();
    formData.append('web_registration_form', 'true');
    formData.append('email', email);
    formData.append('password', password);
    formData.append('g-recaptcha-response', recaptchaToken);
    formData.append('checkbox_agreed_to_terms', 'on');
    formData.append(honeyPot, honeyPot);
    formData.append('plan', String(plan));
    formData.append('authenticity_token', String(formAuthToken));

    if (isPremium) {
      formData.append('premium', 'true');
    }
    if (!isNil(clubId)) {
      formData.append('club_id', String(clubId));
    }
    if (!isNil(challengeId)) {
      formData.append('challenge_id', String(challengeId));
    }
    if (!isNil(autoJoinChallenge)) {
      formData.append('auto_join_challenge', String(autoJoinChallenge));
    }

    try {
      const response = await submitFormData({
        formData,
        url: authorizationUrl,
        logError
      });

      if ('error' in response) {
        // join request failed server-side validations,
        // flash message is populated and shown if an error occurred
        setAlert({
          severity: 'error',
          message: response.error
        });
        setIsProcessing(false); // let user try again
      } else {
        const { pathType, load } = response;
        track({
          action: 'redirect',
          element,
          properties: {
            path_type: pathType,
            redirect_url: load
          }
        });

        // navigate to onboarding flow if no errors were encountered
        window.location.href = load;
      }
    } catch (error) {
      logError(error);

      // generic "something went wrong" alert
      setAlert({
        severity: 'error',
        message: t(`${i18nPrefix}.registration.error_message`)
      });
      setIsProcessing(false); // let user try again
    }
  };

  /**
   * Validate athlete email -
   * 1 - value should be provided (required)
   * 2 - valid email address should be entered (eg. should have @)
   * 3 - email address should not be already registered on strava
   *
   * @returns {Object} result
   * @returns {boolean} result.isValidEmail - true when the email has passed all validation criteria
   * @returns {string} result.isExistingEmail - true when the email is already registered on strava
   * @returns {string} result.message - string message to display to athlete when an error occurs
   */
  const validateEmail = async () => {
    const { email } = formInput;
    if (emailValidationState) {
      // avoid validating email if there is already an error that is not addressed by the athlete yet
      // this prevents making an unnecessary api call to check if email is already registered (see #isAlreadyRegisteredEmail)
      return emailValidationState;
    } else {
      let isValidEmail = true;
      let isExistingEmail = false;
      let message = null;

      if (!(email && email.length > 0)) {
        message = t(`${i18nPrefix}.registration.email_required`);
        isValidEmail = false;
      } else if (!EmailValidator.validate(email)) {
        message = t(`${i18nPrefix}.registration.email_invalid`);
        isValidEmail = false;
      } else {
        try {
          const isUniqueEmail = await isAlreadyRegisteredEmail(email);
          if (!isUniqueEmail) {
            message = t(`${i18nPrefix}.registration.email_taken`);
            isExistingEmail = true;
          }
        } catch (error) {
          logError(error);
          isValidEmail = false;

          // generic "something went wrong" alert
          setAlert({
            severity: 'error',
            message: t(`${i18nPrefix}.registration.error_message`)
          });
        }
      }
      setEmailValidationState({ isExistingEmail, isValidEmail, message });
      return { message, isValidEmail, isExistingEmail };
    }
  };

  /**
   * Validates athlete password
   * - value should be provided (required)
   * - max length of 40 chars
   * - min length of 8 chars
   *
   * @returns {boolean} result true when the password is valid
   */
  const validatePassword = () => {
    const { password } = formInput;

    if (!passwordErrorMsg) {
      // avoid validating email if there is already an error that is not addressed by the athlete yet
      let isValidPassword = true;
      let passwordValidationMsg = null;
      if (!(password && password.length > 0)) {
        passwordValidationMsg = t(
          `${i18nPrefix}.registration.password_required`
        );
        isValidPassword = false;
      } else if (password.length > 40) {
        passwordValidationMsg = t(
          `${i18nPrefix}.registration.password_max_length`
        );
        isValidPassword = false;
      } else if (password.length < 8) {
        passwordValidationMsg = t(
          `${i18nPrefix}.registration.password_min_length`
        );
        isValidPassword = false;
      }
      setPasswordErrMsg(passwordValidationMsg);
      return isValidPassword;
    }
    return false;
  };

  const validateForm = async () => {
    return {
      ...(await validateEmail()),
      isValidPassword: validatePassword()
    };
  };

  /**
   *  Actions to perform when a user hits the "sign up" button (account registration using email).
   *  - validate user input (email and password)
   *  - submit form if input is valid
   *  - focus on invalid inputs if input is invalid
   */
  const handleFormSubmission = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    setIsProcessing(true); // prevent impatient submit clicks

    const { isValidEmail, isValidPassword, isExistingEmail } =
      await validateForm();

    track({
      action: 'click',
      element: 'signup',
      properties: {
        is_valid_email: isValidEmail,
        is_valid_password: isValidPassword,
        is_existing_email: isExistingEmail
      }
    });

    if (isValidEmail && !isExistingEmail && isValidPassword) {
      handleEmailFormSubmit();
    } else {
      setIsProcessing(false); // let user try again

      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      if (!isValidPassword) {
        passwordInput?.current?.focus();
      }
      // prioritize focus on email input if both password and email inputs are invalid
      if (!isValidEmail || isExistingEmail) {
        emailInput?.current?.focus();
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    const { name } = target;

    // save honeyPot input as 'honeyPot' instead of whatever the value of honeyPot is
    // (server determines honeyPot value)
    setFormInput((prev) => {
      return { ...prev, [name === honeyPot ? 'honeyPot' : name]: target.value };
    });

    // clear input specific errors
    if (name === 'email' && emailValidationState?.message) {
      setEmailValidationState(null);
    } else if (passwordErrorMsg) {
      setPasswordErrMsg(null);
    }
    // clear alerts on all input changes
    setAlert(null);
  };

  return (
    <form
      className={className}
      onSubmit={handleFormSubmission}
      noValidate={true}
    >
      <RegistrationInput
        label={t(`${i18nPrefix}.registration.email`)}
        id={`${idPrefix}-email`}
        data-cy="email"
        name="email"
        type="email"
        ref={emailInput}
        placeholder={t(`${i18nPrefix}.registration.email_v2`)}
        onChange={handleInputChange}
        onBlur={validateEmail}
        errorMessage={emailValidationState?.message}
        errorMessageTestId="email-error-message"
        autoComplete="username" // https://html.spec.whatwg.org/multipage/forms.html#autofilling-form-controls:-the-autocomplete-attribute
        theme={theme}
      />

      <RegistrationInput
        label={t(`${i18nPrefix}.registration.password`)}
        id={`${idPrefix}-new-password`}
        data-cy="password"
        name="password"
        type="password"
        ref={passwordInput}
        placeholder={t(`${i18nPrefix}.registration.password_v2`)}
        onChange={handleInputChange}
        onBlur={validatePassword}
        inputMessage={t(
          `${i18nPrefix}.registration.password_length_requirement`
        )}
        errorMessage={passwordErrorMsg}
        errorMessageTestId="password-error-message"
        autoComplete="new-password" // https://html.spec.whatwg.org/multipage/forms.html#autofilling-form-controls:-the-autocomplete-attribute
        showPasswordText={`${i18nPrefix}.registration.show_password`}
        hidePasswordText={`${i18nPrefix}.registration.hide_password`}
        theme={theme}
      />

      {/**
       * Honeypot input is hidden to real users but visible to bots.
       * It should be a value that seems legit to bots but one we don't actually use/care about.
       * If the input is populated, we know it's a bot. */}
      <div style={{ display: 'none' }}>
        <label htmlFor={`${idPrefix}-${honeyPot}`}>{honeyPot}</label>
        <input
          name={honeyPot}
          id={`${idPrefix}-${honeyPot}`}
          onChange={handleInputChange}
        />
      </div>

      {useRecaptcha && (
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={reCaptchaSiteKey}
          size="invisible"
        />
      )}

      <Button
        type="submit"
        variant="primary"
        id={`${idPrefix}-signup-button`}
        data-cy="email-signup-button"
        className={styles.submitButton}
        disabled={isProcessing}
      >
        {isProcessing ? <Spinner /> : t(`${i18nPrefix}.registration.sign_up`)}
      </Button>
    </form>
  );
};

export default EmailRegistrationForm;
